import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Flex,
  SimpleGrid,
  Spinner,
  Stack,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { useAuth } from 'contexts/AuthContext';
import { useUser } from 'contexts/UserContext';
import CInput from 'components/CInput';
import CRadio from 'components/CRadio';
import CButton from 'components/CButton';
import ImageUpload from 'components/ImageUpload';
import { isValidDOB } from 'helpers/helper';

const userRoles = [
  { value: "admin", label: "Admin" },
  { value: "receptionist", label: "Receptionist" },
  { value: "trainer", label: "Trainer" },
];

export default function NewUser() {
  const { toast, auth } = useAuth();
  const { getSingleUser, createUser, updateUser, } = useUser();
  const navigate = useNavigate();
  const params = useParams();

  const textColor = useColorModeValue('secondaryGray.900', 'white');

  const [validated, setValidated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [userDetail, setUserDetail] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    confirmPassword: "",
    dob: "",
    role: "",
    is_locked: false,
    image: "",
    is_web: true,
    is_super_admin: false,
  });

  useEffect(() => {
    fetchSingleUser();
  }, [params.id]);

  const handleClose = () => {
    setUserDetail({
      first_name: "",
      last_name: "",
      email: "",
      password: "",
      confirmPassword: "",
      dob: "",
      role: "",
      image: "",
      is_super_admin: false,
    });
  }

  const fetchSingleUser = async () => {
    setIsLoading(true);
    try {
      if (params.id) {
        const user = await getSingleUser(params.id)
        if (user) {
          setUserDetail(user);
        }
      }
    } catch (error) {
      console.error("Failed to fetch members:", error);
    } finally {
      setIsLoading(false);
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidated(true);

    if (params.id) {
      if (!userDetail.email || !userDetail.first_name || !userDetail.last_name || !userDetail.dob || !userDetail.role || !isValidDOB(userDetail.dob, 16)) return;
    } else {
      if (!userDetail.email || !userDetail.first_name || !userDetail.last_name || !userDetail.password || !userDetail.dob || !userDetail.role || !isValidDOB(userDetail.dob, 16)) return;
    }

    try {
      const formData = new FormData();
      formData.append('first_name', userDetail.first_name);
      formData.append('last_name', userDetail.last_name);
      formData.append('email', userDetail.email);
      formData.append('password', userDetail.password);
      formData.append('confirmPassword', userDetail.confirmPassword);
      formData.append('dob', userDetail.dob);
      formData.append('role', userDetail.role);
      formData.append('is_locked', userDetail.is_locked ? 1 : 0);
      formData.append('is_super_admin', userDetail.is_super_admin ? 1 : 0);
      formData.append('image', userDetail.image ? userDetail.image : '');
      if (params.id) {
        const data = await updateUser(params.id, formData);
        if (!data.error) {
          navigate('/admin/staff');
          handleClose()
        }
      } else {
        if (!userDetail.password || !userDetail.confirmPassword) return;

        if (userDetail.password !== userDetail.confirmPassword) {
          toast({ title: 'Password and Confirm Password does not match.', position: 'top', status: 'error', isClosable: true });
          return;
        }

        const data = await createUser(formData);
        if (!data.error) {
          navigate('/admin/staff');
          handleClose()
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <Flex
      direction="column"
      minH="100vh"
      align="start"
      top="0"
      pt={{ sm: '125px', lg: '75px' }}
      position="relative"
      w="100%"
    >
      <Tabs w="100%">
        <TabPanels mt="24px" maxW="100%" w="100%" mx="auto">
          <TabPanel w="100%" p="0px" mx="auto">
            <Text color={textColor} fontSize="2xl" fontWeight="700" mb="20px">
              User Info
            </Text>
            {isLoading ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                borderRadius="10px"
                overflow="hidden"
                minHeight="500px"
              >
                <Spinner color="#fa8f03" />
              </Box>
            ) : (
              <form onSubmit={handleSubmit}>
                <Flex direction="column" w="100%">
                  <Stack direction="column" spacing="20px">
                    <SimpleGrid columns={{ base: 1, md: 2 }} gap="20px" w="100%">
                      <CInput
                        label="First Name"
                        type="text"
                        value={userDetail.first_name}
                        onChange={(value) => setUserDetail({ ...userDetail, first_name: value })}
                        required={true}
                        validated={validated}
                        placeholder="e.g., Esthera"
                        w="100%"
                      />
                      <CInput
                        label="Last Name"
                        type="text"
                        value={userDetail.last_name}
                        onChange={(value) => setUserDetail({ ...userDetail, last_name: value })}
                        required={true}
                        validated={validated}
                        placeholder="e.g., Deo"
                        w="100%"
                      />
                      <CInput
                        label="Email"
                        type="text"
                        value={userDetail.email}
                        onChange={(value) => setUserDetail({ ...userDetail, email: value })}
                        required={true}
                        validated={validated}
                        placeholder="e.g., mail@sample.com"
                        w="100%"
                        disabled={Boolean(params.id)}
                      />
                      <CInput
                        label="Date of Birth"
                        type="date"
                        value={userDetail.dob}
                        onChange={(value) => setUserDetail({ ...userDetail, dob: value })}
                        required={true}
                        validated={validated}
                        w="100%"
                        max={new Date().toISOString().split("T")[0]}
                      />
                      {
                        !params.id && (
                          <>
                            <CInput
                              label="Password"
                              type="password"
                              value={userDetail.password}
                              onChange={(value) => setUserDetail({ ...userDetail, password: value })}
                              required={true}
                              validated={validated}
                              validationRules={{ minLength: 8 }}
                              placeholder="Min. 8 characters"
                              w="100%"
                            />
                            <CInput
                              label="Confirm Password"
                              type="password"
                              value={userDetail.confirmPassword}
                              onChange={(value) => setUserDetail({ ...userDetail, confirmPassword: value })}
                              required={true}
                              validated={validated}
                              placeholder="Re-enter password"
                              w="100%"
                            />
                          </>
                        )
                      }

                      <div>
                        <div style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center"
                        }}>
                          <CInput
                            label="Lock"
                            type="switch"
                            colorScheme="orange"
                            value={userDetail.is_locked}
                            onChange={(value) =>
                              setUserDetail({
                                ...userDetail,
                                is_locked: value,
                              })
                            }
                            validated={validated}
                            w="100%"
                            mb="8px !important"
                          />
                        </div>
                        {
                          auth.user?.is_super_admin === 1 && userDetail.role === 'admin' && (
                            <div style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center"
                            }}>
                              <CInput
                                label="Make Super Admin"
                                type="switch"
                                colorScheme="orange"
                                value={userDetail.is_super_admin}
                                onChange={(value) =>
                                  setUserDetail({
                                    ...userDetail,
                                    is_super_admin: value,
                                  })
                                }
                                validated={validated}
                                w="100%"
                                mb="8px !important"
                              />
                            </div>
                          )
                        }
                      </div>
                      <CRadio
                        label="User Role"
                        options={userRoles}
                        value={userDetail.role}
                        onChange={(value) => {
                          setUserDetail((prev) => {
                            let updatedDetails = { ...prev, role: value };
                            if (prev.role === "admin" && value !== "admin") {
                              updatedDetails.is_super_admin = false;
                            }

                            return updatedDetails;
                          });
                        }}
                        required={true}
                        isInvalid={validated && !userDetail.role}
                        errorMessage="Please select a user role."
                        disabled={Boolean(params.id)}
                      />
                      <ImageUpload
                        label="Image"
                        existingImage={userDetail.image && userDetail.image instanceof File ? URL.createObjectURL(userDetail.image) : userDetail.image}
                        onImageSelect={(file) => setUserDetail({ ...userDetail, image: file })}
                      />
                    </SimpleGrid>

                  </Stack>
                  <Flex justify="flex-end" align="center" mt="44px" gap="3">
                    <Button
                      variant="darkBrand"
                      fontSize="sm"
                      borderRadius="10px"
                      w={{ base: '128px', md: '148px' }}
                      h="46px"
                      type="button"
                      bg="gray"
                      color="white"
                      _hover={{ bg: 'gray.600' }}
                      onClick={() => {
                        navigate('/admin/staff')
                        handleClose()
                      }}
                    >
                      Cancel
                    </Button>
                    <CButton
                      w={{ base: '128px', md: '148px' }}
                      h="46px"
                      type="submit"
                    >
                      Submit
                    </CButton>
                  </Flex>
                </Flex>
              </form>
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Flex>
  );
}
